import React, {useState, useEffect, Fragment} from "react"
import TextField from "@material-ui/core/TextField"
import Autocomplete, {createFilterOptions} from "@material-ui/lab/Autocomplete"
import {makeStyles} from "@material-ui/core/styles"
import {Typography, ListSubheader, Checkbox} from "@material-ui/core"
import ListboxComponent from "./ListboxComponent"
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank"
import CheckBoxIcon from "@material-ui/icons/CheckBox"
import {useTranslation} from "react-i18next"
import "../i18n"

const useStylesAutocomplete = makeStyles((theme) => ({
  listbox: {
    boxSizing: `border-box`,
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
  inputRoot: {
    backgroundColor: theme.palette.background.box,
  },
  input: {
    marginLeft: 12,
    marginRight: 60,
  },
  endAdornment: {
    top: `auto`,
  },
}))

const useStylesRenderInput = makeStyles({
  root: {
    "&>div": {
      padding: `0!important`,
    },
  },
})

const renderGroup = (params) => [
  <ListSubheader key={params.key} component="div">
    {params.group}
  </ListSubheader>,
  params.children,
]

const icon = <CheckBoxOutlineBlankIcon fontSize="large" />
const checkedIcon = <CheckBoxIcon fontSize="large" />

export default function VirtualSelect({
  id = null,
  required = false,
  key = true,
  multiple = false,
  options,
  value = null,
  label,
  inputLabel,
  onChange,
  target = ``,
  index = 0,
}) {
  const autocompleteClasses = useStylesAutocomplete()
  const renderInputClasses = useStylesRenderInput()
  const {t} = useTranslation()
  const defaultValidation = required
    ? value[index]?.key || value[index]?.value || value?.key || value?.value
      ? false
      : true
    : false
  const [validation, setValidation] = useState(defaultValidation)
  const filterOptions = (options, params) => {
    const filter = createFilterOptions({
      // matchFrom: `start`,
      stringify: (option) => {
        const label = option?.label ?? ``
        const labelEn = option?.labelEn ?? ``
        const labelJa = option?.labelJa ?? ``
        // const region = option?.region ?? ``
        // if (region !== filterString) return ``
        return label + labelEn + labelJa
      },
    })
    const filtered = filter(options, params)
    return filtered.filter((option) => option[label] || option?.value || option?.key || option?.labelEn || option?.labelJa)
  }

  const renderOption = (option, {selected = undefined}) => {
    return multiple ? (
      <Fragment>
        <Checkbox
          icon={icon}
          checkedIcon={checkedIcon}
          style={{
            marginRight: 8,
          }}
          checked={selected}
        />
        {option[label]}
      </Fragment>
    ) : (
      <Typography noWrap>{option[label]}</Typography>
    )
  }

  const getOptions = (options, value) => {
    // 空の場合 (value = {key:null, label:""}) を考慮
    // ここで追加したものは filterOptions でフィルタリング
    try {
      if (!value) return options
      if (Array.isArray(value) && (value[index] ?? null) === null) return options
      if (options.map((option) => option[label]).includes(Array.isArray(value) ? value[index][label] : value[label])) {
        return options
      }
      return options.concat(Array.isArray(value) ? value[index] : value)
    } catch (e) {
      console.log(e)
    }
  }

  const validateValue = (value) => {
    // 値の入力がない
    if ((value ?? null) == null || (value[label] ?? null) === null || value[label] === ``) {
      setValidation(required)
      return
    }
    
    //入力値チェック
    const isExists = options.map((option) => option[label]).includes(value[label])
    if (isExists) {
      if (required && validation)
        setValidation(false)
    } else {
      if (required && !validation)
        setValidation(true)
      onChange(null)
    }
  }

  useEffect(() => {
    // options に value が含まれていない場合、nullに更新
    validateValue(Array.isArray(value) ? value[index] : value)
  }, [options, value, index])

  const getOptionLabel = (option) => {
    if (typeof option === `string`) {
      return option
    }
    if (Array.isArray(option) && typeof option[index] === `string`) {
      return option[index]
    }
    return Array.isArray(option) ? option[index][label] : option[label]
  }

  const renderInput = (params) => {
    return (
      <TextField
        {...params}
        classes={renderInputClasses}
        className={target}
        label={inputLabel}
        error={validation}
        helperText={validation ? t(`requiredItem`) : null}
      ></TextField>
    )
  }

  const getOptionSelected = (option, value) => {
    if (Array.isArray(value) ? option[label] === value[index][label] : option[label] === value[label]) {
      return Array.isArray(value) ? option[label] === value[index][label] : option[label] === value[label]
    }
  }

  const onInputChange = (value, reason) => {
    console.log(value, reason)
  }

  const onChangeWrap = (newValue) => {
    setValidation(newValue ? false : true)
    if (Array.isArray(value)) {
      value[index] = newValue || ``
    } else {
      value = newValue || ``
    }
    onChange(newValue)
  }

  return (
    <Autocomplete
      id={id}
      key={key}
      multiple={multiple}
      blurOnSelect="touch"
      style={{
        margin: `16px 0px`,
      }}
      filterOptions={filterOptions}
      classes={autocompleteClasses}
      className={target}
      ListboxComponent={ListboxComponent}
      renderGroup={renderGroup}
      value={Array.isArray(value) ? value[index] : value}
      options={getOptions(options, value)}
      getOptionLabel={(option) => getOptionLabel(option)}
      renderInput={(params) => renderInput(params)}
      renderOption={(option, {selected}) =>
        renderOption(option, {
          selected,
        })
      }
      onChange={(e, newValue) => onChangeWrap(newValue)}
      onInputChange={(e, newValue, reason) => onInputChange(newValue, reason)}
      getOptionSelected={(option, val) => getOptionSelected(option, val)}
    />
  )
}
